import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import BottomNavigation from '../customComponents/BottomNavigation';
import LoginRequiredModal from '../components/LoginRequiredModal';
import { useNavigate } from 'react-router-dom';

const LayoutContainer = styled.div<{ 
  $showBottomNav: boolean; 
  $customPaddingTop?: number;
}>`
  display: flex;
  flex-direction: column;
  min-height: ${props => props.$showBottomNav ? 'calc(100vh - var(--bottom-nav-height, 90px))' : '100vh'}; /* 바텀 네비게이션 높이 제외 */
  padding-bottom: ${props => props.$showBottomNav ? 'var(--bottom-nav-height, 90px)' : '0'}; /* 바텀 네비게이션 높이 만큼 패딩 추가 */
  padding-top: 90px; /* 웹 앱바 높이 고정값 */

  @media (max-width: 767px) {
    min-height: ${props => props.$showBottomNav ? 'calc(100vh - var(--bottom-nav-height, 90px))' : '100vh'}; /* 바텀 네비게이션 높이 제외 */
    padding-top: ${props => props.$customPaddingTop || 0}px; /* 커스텀 패딩이 있으면 사용, 없으면 기본 60px */
  }

  /* Flutter WebView 환경에서는 SafeArea에서 이미 시스템 인셋과 하단 영역을 처리하므로
     레이아웃 컨테이너의 추가 하단 패딩을 제거하여 여백을 줄임 */
  @media (max-width: 767px) {
    html.flutter-webview &,
    body.flutter-webview & {
      padding-bottom: 0;
    }
  }

  @media (min-width: 768px) {
    min-height: auto; /* 웹에서는 자연스러운 높이 사용 */
    padding-bottom: 0;
  }
`;

interface MainLayoutProps {
  children: React.ReactNode;
  currentPage?: 'live-auctions' | 'auction' | 'home' | 'favorites' | 'mypage';
  paddingTop?: number; // 모바일에서 커스텀 상단 패딩 (px)
}

const MainLayout: React.FC<MainLayoutProps> = ({ children, currentPage, paddingTop }) => {
  const navigate = useNavigate();
  const [showLoginModal, setShowLoginModal] = useState(false);

  // 바텀 네비게이션을 표시할 페이지들
  const showBottomNav = currentPage && ['live-auctions', 'auction', 'home', 'favorites', 'mypage'].includes(currentPage);

  // 실제 바텀 네비게이션 DOM 높이를 기반으로 CSS 변수 동기화
  useEffect(() => {
    if (!showBottomNav) return;

    const updateBottomNavHeight = () => {
      const navElement = document.getElementById('bottom-navigation');
      if (!navElement) return;

      const height = navElement.offsetHeight;
      if (height > 0) {
        document.documentElement.style.setProperty('--bottom-nav-height', `${height}px`);
      }
    };

    updateBottomNavHeight();
    window.addEventListener('resize', updateBottomNavHeight);

    return () => {
      window.removeEventListener('resize', updateBottomNavHeight);
    };
  }, [showBottomNav]);

  const handleLoginRequired = () => {
    setShowLoginModal(true);
  };

  const handleCloseModal = () => {
    setShowLoginModal(false);
  };

  const handleLoginFromModal = () => {
    setShowLoginModal(false);
    navigate('/login');
  };

  return (
    <>
      <LayoutContainer 
        $showBottomNav={!!showBottomNav}
        $customPaddingTop={paddingTop}
      >
        {children}
      </LayoutContainer>
      
      {showBottomNav && (
        <BottomNavigation
          currentPage={currentPage}
          onLoginRequired={handleLoginRequired}
        />
      )}
      
      <LoginRequiredModal
        isOpen={showLoginModal}
        onClose={handleCloseModal}
        onLogin={handleLoginFromModal}
      />
    </>
  );
};

export default MainLayout;
